/**
 * Consumer-facing slice accessors for BookingContext.
 *
 * These hooks read a focused subset of the composed booking provider value so
 * pages can depend on only the booking domain they need. Internal data loading
 * and mutations live in sibling modules; this file is the public read/mutation
 * boundary for React components.
 */
import { useContext } from 'react';
import { BookingContext } from '../../context/BookingContext';

function useBookingSlice(selector, hookName) {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error(`${hookName} must be used within a BookingProvider`);
  }
  return selector(context);
}

/** Booking list and loading state for the active facility. */
export function useBookings() {
  return useBookingSlice(
    ({ bookings, loading, reloadBookings }) => ({ bookings, loading, reloadBookings }),
    'useBookings'
  );
}

/** Create, update, cancel, and archive booking mutations. */
export function useBookingActions() {
  return useBookingSlice(
    ({ addBooking, cancelBooking, archiveBooking, updateBookingStatus }) => ({
      addBooking,
      cancelBooking,
      archiveBooking,
      updateBookingStatus,
    }),
    'useBookingActions'
  );
}

/** Slot availability and pricing helpers. */
export function useSlotAvailability(date, groundId, serviceId) {
  const helpers = useBookingSlice(
    ({ getSlotAvailability, calculateSlotPrice, getGroundById, getServiceById, getAllServices, getSlotDetails, getSeasonById }) => ({
      getSlotAvailability,
      calculateSlotPrice,
      getGroundById,
      getServiceById,
      getAllServices,
      getSlotDetails,
      getSeasonById,
    }),
    'useSlotAvailability'
  );

  if (date != null && groundId != null && serviceId != null) {
    return {
      ...helpers,
      availability: helpers.getSlotAvailability(date, groundId, serviceId),
    };
  }

  return helpers;
}

/** Payment recording and history for a booking. */
export function usePayments(bookingId) {
  const payments = useBookingSlice(
    ({ markPaymentComplete, fetchPaymentHistory, paymentHistory, historyLoading }) => ({
      markPaymentComplete,
      fetchPaymentHistory,
      paymentHistory,
      historyLoading,
    }),
    'usePayments'
  );

  return payments;
}

/** Slot configuration state and mutations. */
export function useSlotConfiguration() {
  return useBookingSlice(
    ({
      slotConfiguration,
      updateSlotConfiguration,
      addServiceConfiguration,
      updateServiceConfiguration,
      removeServiceConfiguration,
      clearServiceConfig,
    }) => ({
      slotConfiguration,
      updateSlotConfiguration,
      addServiceConfiguration,
      updateServiceConfiguration,
      removeServiceConfiguration,
      clearServiceConfig,
    }),
    'useSlotConfiguration'
  );
}

/** Facility and grounds data. */
export function useFacilityData() {
  return useBookingSlice(
    ({ facility, grounds, loading, updateFacility, clearAllData }) => ({
      facility,
      grounds,
      loading,
      updateFacility,
      clearAllData,
    }),
    'useFacilityData'
  );
}
